import { Injectable } from '@angular/core';
import { State, Selector, Action, StateContext, createSelector } from '@ngxs/store';
import { Observable, of, forkJoin, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { PulseService } from 'ng2-app/pulse/pulse.service';
import { normalizeRealTimeScores, normalizeHistoricalScores } from 'ng2-app/pulse/pulse.utils';
import { TeamRealTimeScoresResponse } from 'ng2-app/models/pulse/team-scores.model';
import { LenderUserHistoricalScores } from 'ng2-app/models/pulse/scores.model';
import { GetScoresByTeamId, GetRealTimeScoresByTeamId } from 'ng2-app/store/pulse/pulse.action';
import { PulseHistoricalParams } from 'ng2-app/models/pulse/pulse.type';
import {
  QueryStateBase,
  setQueryLoading,
  setQuerySuccess,
  setQueryError,
  isQueryLoading,
  isQueryFetching,
} from 'ng2-app/store/query-state.model';

export interface TeamScoreGroup {
  realTimeScores: TeamRealTimeScoresResponse | null;
  historicalScores: LenderUserHistoricalScores[];
}

export type TeamLastSync = {
  realTimeScores: Date | null;
  historicalScores: Date | null;
};

export interface TeamQueryEntry extends QueryStateBase<TeamScoreGroup> {
  lastSync: TeamLastSync;
  lastHistoricalParams?: PulseHistoricalParams;
}

export interface PulseTeamStateModel {
  queries: { [teamId: number]: TeamQueryEntry };
}

const DEFAULT_DATA: TeamScoreGroup = { realTimeScores: null, historicalScores: [] };
export const TEAM_REALTIME_TTL = 1000 * 60 * 5; // 5 minutes

function createTeamQuery(): TeamQueryEntry {
  return {
    data: { ...DEFAULT_DATA, historicalScores: [] },
    status: 'idle',
    isFetching: false,
    error: null,
    lastSync: { realTimeScores: null, historicalScores: null },
  };
}

function isSameDay(date: Date): boolean {
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate() &&
    now.getHours() > 3 // Cron for historical scores runs at 2am
  );
}

@State<PulseTeamStateModel>({
  name: 'pulseTeam',
  defaults: {
    queries: {},
  },
})
@Injectable()
export class PulseTeamState {
  constructor(private pulseService: PulseService) {}

  // --- Selectors ---

  @Selector()
  static queries(state: PulseTeamStateModel) {
    return state?.queries ?? null;
  }

  static teamQuery(teamId: number) {
    return createSelector(
      [PulseTeamState.queries],
      (queries: PulseTeamStateModel['queries']) =>
        queries[teamId] ?? createTeamQuery()
    );
  }

  static teamData(teamId: number) {
    return createSelector(
      [PulseTeamState.teamQuery(teamId)],
      (query: TeamQueryEntry) => query.data
    );
  }

  static teamIsLoading(teamId: number) {
    return createSelector(
      [PulseTeamState.teamQuery(teamId)],
      (query: TeamQueryEntry) => isQueryLoading(query)
    );
  }

  static teamIsFetching(teamId: number) {
    return createSelector(
      [PulseTeamState.teamQuery(teamId)],
      (query: TeamQueryEntry) => isQueryFetching(query)
    );
  }

  static teamLastSync(teamId: number) {
    return createSelector(
      [PulseTeamState.teamQuery(teamId)],
      (query: TeamQueryEntry) => query.lastSync.realTimeScores
    );
  }

  // --- Actions ---

  @Action(GetScoresByTeamId)
  getScoresByTeamId(
    ctx: StateContext<PulseTeamStateModel>,
    { teamId, overrideCache = false, historicalParams }: GetScoresByTeamId
  ): Observable<TeamScoreGroup> {
    const state = ctx.getState();
    const query = state?.queries[teamId] ?? createTeamQuery();

    const needsRealTime = !this.isRealTimeCacheValid(query, overrideCache);
    const needsHistorical = !this.isHistoricalCacheValid(query, historicalParams);

    if (!needsRealTime && !needsHistorical) {
      return of(query.data);
    }

    // Set loading state
    ctx.patchState({
      queries: {
        ...state?.queries,
        [teamId]: { ...query, ...setQueryLoading(query) },
      },
    });

    const realTimeScores$ = needsRealTime
      ? this.pulseService.getRealTimeScoresByTeamId(teamId)
      : of(query.data.realTimeScores);

    const historicalScores$ = needsHistorical
      ? this.pulseService.getHistoricalScoresByTeamId(teamId, historicalParams)
      : of(query.data.historicalScores);

    return forkJoin({ realTimeScores: realTimeScores$, historicalScores: historicalScores$ }).pipe(
      tap(data => {
        if (data.realTimeScores) {
          for (const user of data.realTimeScores.lenderUsers) normalizeRealTimeScores(user);
        }
        normalizeHistoricalScores(data.historicalScores as LenderUserHistoricalScores[]);
        const now = new Date();
        const currentQuery = ctx.getState().queries[teamId] ?? createTeamQuery();
        ctx.patchState({
          queries: {
            ...ctx.getState().queries,
            [teamId]: {
              ...currentQuery,
              ...setQuerySuccess(data),
              lastSync: {
                realTimeScores: needsRealTime ? now : currentQuery.lastSync.realTimeScores,
                historicalScores: needsHistorical ? now : currentQuery.lastSync.historicalScores,
              },
              lastHistoricalParams: needsHistorical ? historicalParams : currentQuery.lastHistoricalParams,
            },
          },
        });
      }),
      catchError(error => {
        const currentQuery = ctx.getState().queries[teamId] ?? createTeamQuery();
        ctx.patchState({
          queries: {
            ...ctx.getState().queries,
            [teamId]: { ...currentQuery, ...setQueryError(error) },
          },
        });
        return throwError(() => error);
      })
    );
  }

  @Action(GetRealTimeScoresByTeamId)
  getRealTimeScoresByTeamId(
    ctx: StateContext<PulseTeamStateModel>,
    { teamId, overrideCache = false }: GetRealTimeScoresByTeamId
  ): Observable<TeamRealTimeScoresResponse | null> {
    const state = ctx.getState();
    const query = state?.queries[teamId] ?? createTeamQuery();

    if (this.isRealTimeCacheValid(query, overrideCache)) {
      return of(query.data.realTimeScores);
    }

    // Set loading state
    ctx.patchState({
      queries: {
        ...state?.queries,
        [teamId]: { ...query, ...setQueryLoading(query) },
      },
    });

    return this.pulseService.getRealTimeScoresByTeamId(teamId).pipe(
      tap(realTimeScores => {
        if (realTimeScores) {
          for (const user of realTimeScores.lenderUsers) normalizeRealTimeScores(user);
        }
        const now = new Date();
        const currentQuery = ctx.getState().queries[teamId] ?? createTeamQuery();
        ctx.patchState({
          queries: {
            ...ctx.getState().queries,
            [teamId]: {
              ...currentQuery,
              ...setQuerySuccess({ ...currentQuery.data, realTimeScores }),
              lastSync: { ...currentQuery.lastSync, realTimeScores: now },
            },
          },
        });
      }),
      catchError(error => {
        const currentQuery = ctx.getState().queries[teamId] ?? createTeamQuery();
        ctx.patchState({
          queries: {
            ...ctx.getState().queries,
            [teamId]: { ...currentQuery, ...setQueryError(error) },
          },
        });
        return throwError(() => error);
      })
    );
  }

  // --- Cache helpers ---

  private isRealTimeCacheValid(query: TeamQueryEntry, forceRefresh: boolean): boolean {
    if (forceRefresh) return false;
    const lastSync = query.lastSync.realTimeScores;
    if (!lastSync) return false;
    return lastSync.getTime() + TEAM_REALTIME_TTL > Date.now();
  }

  private isHistoricalCacheValid(query: TeamQueryEntry, params?: PulseHistoricalParams): boolean {
    const lastSync = query.lastSync.historicalScores;
    if (!lastSync) return false;
    if (!this.historicalParamsMatch(query.lastHistoricalParams, params)) return false;
    return isSameDay(lastSync);
  }

  private historicalParamsMatch(a?: PulseHistoricalParams, b?: PulseHistoricalParams): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    return a.excludeZeroScores === b.excludeZeroScores
      && a.fromDate === b.fromDate
      && a.toDate === b.toDate;
  }
}
